/**
 * Core item definitions for Idle Depths.
 *
 * These are the base items available in the core content pack.
 * Additional items can be added via DLC packs.
 */

import {
  type ItemDefinition,
  ItemType,
  ItemRarity,
  EquipmentSlot,
  isItemDefinition,
} from '@idle-depths/shared';

export { isItemDefinition };

/**
 * Core item definitions.
 *
 * Includes:
 * - 3 weapons
 * - 3 armor pieces
 * - 4 resources
 * - 3 consumables
 * - 2 quest items
 */
export const CORE_ITEMS: ItemDefinition[] = [
  // =========================================
  // WEAPONS
  // =========================================
  {
    id: 'iron_sword',
    name: 'Iron Sword',
    description: 'A basic sword forged from iron. Reliable for beginners.',
    type: ItemType.WEAPON,
    rarity: ItemRarity.COMMON,
    icon: 'Sword',
    value: 50,
    stackable: false,
    maxStackSize: null,
    tradeable: true,
    equipSlot: EquipmentSlot.MAIN_HAND,
    properties: {
      damage: 10,
      attackSpeed: 2.4,
      weaponType: 'melee',
    },
  },
  {
    id: 'steel_dagger',
    name: 'Steel Dagger',
    description: 'A quick and nimble dagger made of refined steel.',
    type: ItemType.WEAPON,
    rarity: ItemRarity.UNCOMMON,
    icon: 'Scissors',
    value: 75,
    stackable: false,
    maxStackSize: null,
    tradeable: true,
    equipSlot: EquipmentSlot.MAIN_HAND,
    properties: {
      damage: 8,
      attackSpeed: 3.0,
      weaponType: 'melee',
      critChance: 0.05,
    },
  },
  {
    id: 'oak_bow',
    name: 'Oak Bow',
    description: 'A sturdy bow crafted from oak wood. Effective at range.',
    type: ItemType.WEAPON,
    rarity: ItemRarity.COMMON,
    icon: 'Target',
    value: 60,
    stackable: false,
    maxStackSize: null,
    tradeable: true,
    equipSlot: EquipmentSlot.MAIN_HAND,
    properties: {
      damage: 12,
      attackSpeed: 1.8,
      weaponType: 'ranged',
      range: 15,
    },
  },

  // =========================================
  // ARMOR
  // =========================================
  {
    id: 'leather_cap',
    name: 'Leather Cap',
    description: 'A simple cap made of treated leather. Provides basic head protection.',
    type: ItemType.ARMOR,
    rarity: ItemRarity.COMMON,
    icon: 'Crown',
    value: 25,
    stackable: false,
    maxStackSize: null,
    tradeable: true,
    equipSlot: EquipmentSlot.HEAD,
    properties: {
      defense: 3,
    },
  },
  {
    id: 'iron_chainmail',
    name: 'Iron Chainmail',
    description: 'Interlocking iron rings provide solid protection for the torso.',
    type: ItemType.ARMOR,
    rarity: ItemRarity.UNCOMMON,
    icon: 'Shield',
    value: 120,
    stackable: false,
    maxStackSize: null,
    tradeable: true,
    equipSlot: EquipmentSlot.BODY,
    properties: {
      defense: 12,
      movementPenalty: 0.05,
    },
  },
  {
    id: 'wooden_shield',
    name: 'Wooden Shield',
    description: 'A round shield carved from hardwood. Blocks incoming attacks.',
    type: ItemType.ARMOR,
    rarity: ItemRarity.COMMON,
    icon: 'ShieldCheck',
    value: 40,
    stackable: false,
    maxStackSize: null,
    tradeable: true,
    equipSlot: EquipmentSlot.OFF_HAND,
    properties: {
      defense: 5,
      blockChance: 0.1,
    },
  },

  // =========================================
  // RESOURCES
  // =========================================
  {
    id: 'oak_logs',
    name: 'Oak Logs',
    description: 'Sturdy logs harvested from oak trees. Used in crafting and construction.',
    type: ItemType.RESOURCE,
    rarity: ItemRarity.COMMON,
    icon: 'TreeDeciduous',
    value: 5,
    stackable: true,
    maxStackSize: 999,
    tradeable: true,
    equipSlot: null,
    properties: {
      skill: 'woodcutting',
      tier: 1,
    },
  },
  {
    id: 'iron_ore',
    name: 'Iron Ore',
    description: 'Raw iron ore extracted from the mines. Can be smelted into bars.',
    type: ItemType.RESOURCE,
    rarity: ItemRarity.COMMON,
    icon: 'Mountain',
    value: 10,
    stackable: true,
    maxStackSize: 999,
    tradeable: true,
    equipSlot: null,
    properties: {
      skill: 'mining',
      tier: 2,
    },
  },
  {
    id: 'copper_ore',
    name: 'Copper Ore',
    description: 'Soft copper ore. The most basic mining resource.',
    type: ItemType.RESOURCE,
    rarity: ItemRarity.COMMON,
    icon: 'Circle',
    value: 3,
    stackable: true,
    maxStackSize: 999,
    tradeable: true,
    equipSlot: null,
    properties: {
      skill: 'mining',
      tier: 1,
    },
  },
  {
    id: 'clay',
    name: 'Clay',
    description: 'Soft clay gathered from riverbeds. Used in pottery and crafting.',
    type: ItemType.RESOURCE,
    rarity: ItemRarity.COMMON,
    icon: 'Droplet',
    value: 2,
    stackable: true,
    maxStackSize: 999,
    tradeable: true,
    equipSlot: null,
    properties: {
      skill: 'crafting',
      tier: 1,
    },
  },

  // =========================================
  // CONSUMABLES
  // =========================================
  {
    id: 'health_potion',
    name: 'Health Potion',
    description: 'A red potion that restores health when consumed.',
    type: ItemType.CONSUMABLE,
    rarity: ItemRarity.COMMON,
    icon: 'Heart',
    value: 20,
    stackable: true,
    maxStackSize: 99,
    tradeable: true,
    equipSlot: null,
    properties: {
      healAmount: 25,
      cooldown: 30,
    },
  },
  {
    id: 'stamina_potion',
    name: 'Stamina Potion',
    description: 'A green potion that restores stamina when consumed.',
    type: ItemType.CONSUMABLE,
    rarity: ItemRarity.COMMON,
    icon: 'Zap',
    value: 15,
    stackable: true,
    maxStackSize: 99,
    tradeable: true,
    equipSlot: null,
    properties: {
      staminaAmount: 30,
      cooldown: 45,
    },
  },
  {
    id: 'bread',
    name: 'Bread',
    description: 'A loaf of freshly baked bread. Restores a small amount of health.',
    type: ItemType.CONSUMABLE,
    rarity: ItemRarity.COMMON,
    icon: 'Cookie',
    value: 5,
    stackable: true,
    maxStackSize: 99,
    tradeable: true,
    equipSlot: null,
    properties: {
      healAmount: 5,
      cooldown: 10,
    },
  },

  // =========================================
  // QUEST ITEMS
  // =========================================
  {
    id: 'mysterious_key',
    name: 'Mysterious Key',
    description: 'An ancient key with strange markings. It seems important.',
    type: ItemType.QUEST,
    rarity: ItemRarity.RARE,
    icon: 'Key',
    value: 0,
    stackable: false,
    maxStackSize: null,
    tradeable: false,
    equipSlot: null,
    properties: {
      questId: 'main_quest_1',
    },
  },
  {
    id: 'ancient_scroll',
    name: 'Ancient Scroll',
    description: 'A weathered scroll containing cryptic writings in an unknown language.',
    type: ItemType.QUEST,
    rarity: ItemRarity.RARE,
    icon: 'Scroll',
    value: 0,
    stackable: false,
    maxStackSize: null,
    tradeable: false,
    equipSlot: null,
    properties: {
      questId: 'main_quest_2',
    },
  },
];
